import React, { useMemo, useState } from 'react';
import { useNutritionPlan } from '@/features/nutrition/hooks/useNutritionPlan';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import { Navigation } from 'swiper/modules';
import { parseISO, differenceInCalendarDays } from 'date-fns';
import './ScheduleSection.css';

interface Meal {
    time: string;
    items: { title: string; hasRecipe: boolean }[];
}

interface NutritionDay {
    day: string;
    isComplete: boolean;
    meals: Meal[];
}

export const NutritionScheduleSection: React.FC = () => {
    const { plan, loading } = useNutritionPlan();
    const [activeWeek, setActiveWeek] = useState(0);

    const weeks: NutritionDay[][] = useMemo(() => {
        if (!plan?.plan?.days) return [];

        const dayNames = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];
        const createdAt = parseISO(plan.created_at);
        const today = new Date();
        const daysPassed = Math.max(0, differenceInCalendarDays(today, createdAt));

        const days: NutritionDay[] = plan.plan.days.map((dayData, index) => ({
            day: dayNames[index % 7],
            isComplete: index < daysPassed,
            meals: [
                {
                    time: 'Завтрак',
                    items: (dayData.morning || []).map(item => ({
                        title: item.name,
                        hasRecipe: true,
                    })),
                },
                {
                    time: 'Обед',
                    items: (dayData.day || []).map(item => ({
                        title: item.name,
                        hasRecipe: true,
                    })),
                },
                {
                    time: 'Ужин',
                    items: (dayData.evening || []).map(item => ({
                        title: item.name,
                        hasRecipe: true,
                    })),
                },
            ],
        }));

        const result: NutritionDay[][] = [];
        for (let i = 0; i < days.length; i += 7) {
            result.push(days.slice(i, i + 7));
        }

        return result;
    }, [plan]);

    if (loading || !plan) return null;

    return (
        <section className="section layout__section program">
            <div className="container">
                <div className="program-intro-wrapper">
                    <div className="program-intro__line" />
                    <div className="programTop">
                        <div className="program-intro__left">
                            <h1 className="program-intro__title">План питания</h1>
                            <div className="program-intro__row">
                                <span className="badge badge_purple">НА 1-Й МЕСЯЦ</span>
                                <span className="program-intro__date">
                                    {new Date(plan.created_at).toLocaleDateString()} – {/* примерная дата завершения */}
                                    {new Date(new Date(plan.created_at).getTime() + 27 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                                </span>
                            </div>
                            <p className="program-intro__subtitle">Подобранная для вас по итогу теста</p>
                        </div>
                        <div style={{ display: 'flex' }}>
                            <div className="program-intro__imageN" style={{ marginRight: '10px' }}>
                                <img src="/img/program-nutrition-image1.png" alt="Питание" />
                            </div>
                            <div className="program-intro__imageN" style={{ marginRight: '10px' }}>
                                <img src="/img/program-nutrition-image2.png" alt="Питание" />
                            </div>
                            <div className="program-intro__imageN" style={{ marginRight: '10px' }}>
                                <img src="/img/program-nutrition-image3.png" alt="Питание" />
                            </div>
                        </div>
                    </div>

                    <div className="program-intro">
                        <div className="program-intro__left">
                            <p className="program-intro__description bold">
                                Вам необходимо придерживаться более сбалансированного питания и снизить количество сахара и вредных углеводов в рационе.
                            </p>
                        </div>
                        <div className="program-intro__center">
                            <p className="program-intro__text">
                                Мы предлагаем персональный план питания, рассчитанный по дням на неделю. Упор будет сделан на свежих овощах, белках и полезных жирах.
                            </p>
                        </div>
                    </div>
                </div>

                <nav className="program-schedule__tabs-nav program-schedule-tabs-nav">
                    <span className="program-schedule-tabs-nav__label">Расписание питания</span>
                    <ul className="tabs-nav__list program-schedule-tabs-nav__list">
                        {weeks.map((_, i) => (
                            <li
                                key={i}
                                className={`tabs-nav__item program-schedule-tabs-nav__item ${activeWeek === i ? '_active' : ''}`}
                                onClick={() => setActiveWeek(i)}
                            >
                                <a className="tabs-nav__link program-schedule-tabs-nav__link">
                                    {i + 1} неделя
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className="program__cards program-cards">
                    <Swiper
                        className="slider program-cards__slider"
                        slidesPerView={'auto'}
                        spaceBetween={30}
                        navigation={{
                            nextEl: '.slider-nav__button_next',
                            prevEl: '.slider-nav__button_prev',
                        }}
                        modules={[Navigation]}
                    >
                        {weeks[activeWeek]?.map((day, idx) => (
                            <SwiperSlide key={idx} style={{ width: 350 }}>
                                <div className="program-card program-card_nutrition">
                                    <header className="program-card__header">
                                        <h4 className="title program-card__title">{day.day}</h4>
                                        {day.isComplete && (
                                            <div className="program-card__badge">
                                                <svg className="svg-icon">
                                                    <use href="/img/sprite-icons.svg#check" />
                                                </svg>
                                            </div>
                                        )}
                                    </header>
                                    <div className="program-card__content">
                                        <ul className="program-card__list program-card__list--grid">
                                            {day.meals.map((meal, i) => (
                                                <li key={i} className="program-card__item program-card-item">
                                                    <span className="program-card-item__time">{meal.time}</span>
                                                    <div className="program-card-item__tasks">
                                                        {meal.items.map((item, j) => (
                                                            <div key={j} className="program-card-item__task program-card-item-task">
                                                                <h5 className="title program-card-item-task__title">{item.title}</h5>
                                                                {item.hasRecipe && (
                                                                    <a className="schedule-table-task__link" data-popup href="#popup-receipt" data-name={item.title}>
                                                                        Рецепт
                                                                    </a>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    <div className="slider__nav slider-nav">
                        <div className="slider-nav__button slider-nav__button_prev">
                            <svg className="svg-icon">
                                <use href="/img/sprite-icons.svg#arrow-left" />
                            </svg>
                        </div>
                        <div className="slider-nav__separator" />
                        <div className="slider-nav__button slider-nav__button_next">
                            <svg className="svg-icon">
                                <use href="/img/sprite-icons.svg#arrow-right" />
                            </svg>
                        </div>
                    </div>

                    <button style={{ display: 'flex', margin: '84px auto 140px' }} className="button">
                        Перейти к списку рецептов
                    </button>
                </div>
            </div>
        </section>
    );
};
